import React, {CSSProperties, useCallback, useMemo, useState} from 'react'
import Lottie from 'lottie-react'
import styled from 'styled-components'
import {useTheme} from 'themes'
import {
  GIFT_SHOP_UPLOAD_TYPE,
  GIFTSHOP_SEARCH_CREATION_BY_FILE_LIMIT_UPLOAD,
  GIFTSHOP_SEARCH_CREATION_CHARACTER_MIN,
  IMAGE_ASSET,
  SERVICE_CANCELLATION_SEARCH_CREATION_CONTENT,
  SERVICE_CANCELLED_RESPONSE,
} from 'consts'
import {translate} from 'i18n'
import {requestData} from 'services'
import {GiftShopContentData, GiftShopSearchCreationState} from 'types'
import {
  getAndSupplyFileMime,
  getBorder,
  handleGetFileSignature,
  showSnackbar,
  useHistory,
} from 'utils'
import {
  Button,
  ButtonIcon,
  DragDrop,
  Icon,
  Image,
  Input,
  ListLazy,
  Paragraph,
} from 'common/components'
import {useDispatch} from 'lib/redux'
import convertUnit from 'lib/unit'
import {TemplateSearchCreationItem} from 'pages/template'
import animationData from '../../../../../assets/lottie/activity_indicator.json'
import {
  GiftShopTemplateSearchCreationModal,
  GiftShopTemplateSearchCreationResetModal,
} from '../../../template'
import {GiftshopProfileSearchCreationContentProps} from './GiftshopProfileSearchCreationContentProps'

const StyledLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: ${convertUnit(10)} 0;
  align-items: center;
  justify-content: center;
  background: ${({theme}) => theme.white_1};
`

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: ${convertUnit(12)};
  margin-bottom: ${convertUnit(4)};
`

const StyledSignatureInput = styled(Input)`
  padding: 0 !important;
`

const StyledDropZoneContainer = styled.div`
  ${({theme}) => ({
    border: getBorder(2, 'dashed', theme.gray_3),
    ':hover': {
      borderColor: theme.primary_5,
    },
  })}
  height: 100%;
  width: 100%;
  border-radius: ${convertUnit(8)};
  flex-direction: column;
  box-sizing: border-box;
  justify-content: center;
  padding: ${convertUnit(100)};
`

const StyledDropZoneButton = styled(Button)`
  margin-top: ${convertUnit(8)};
`

const StyledSignatureReset = styled(ButtonIcon)`
  border-radius: ${convertUnit(8)};
`

const StyledLottie = styled(Lottie)`
  width: ${convertUnit(32)};
  height: ${convertUnit(32)};
`

const StyledListSearchByTitleContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
`

const StyledNotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`

const StyledEmptyImg = styled(Image)`
  object-fit: contain;
  margin-bottom: ${convertUnit(25)};
  max-width: ${convertUnit(200)};
  max-height: ${convertUnit(165)};
`

const StyledEmptyMessage = styled(Paragraph)`
  text-align: center;
`

export default function GiftshopProfileSearchCreationContent({
  stateSearch,
}: GiftshopProfileSearchCreationContentProps) {
  const history = useHistory()
  const [attachedFileModal, setAttachedFileModal] = useState(false)
  const [resetSearchModal, setResetSearchModal] = useState(false)
  const stateData = useState<ReadonlyArray<GiftShopContentData>>([])
  const data = stateData[0]
  const [search, setSearch] = stateSearch
  const {status, lastQueryChange} = search
  const foundSignatures = useMemo(
    () =>
      data
        .map((file) => file.signature)
        .filter((file) => file !== undefined) as string[],
    [data],
  )

  const {update} = useDispatch()
  const theme = useTheme()
  const emptyListImage = IMAGE_ASSET('giftshop', 'not-found.png')

  const handleKeyExtractor = useCallback(
    (item: GiftShopContentData) => item.content_id,
    [],
  )

  const handleLoadData = useCallback(
    async (page: number, limit: number) => {
      if (
        search.files.length === 0 ||
        search.files
          .map((file) => file.signature)
          .filter((signature) => !!signature).length === 0
      )
        return []

      const response = await requestData('giftshop_get_creation_signature', {
        useDefaultMessage: true,
        actionType: 'fetch',
        cancelId: SERVICE_CANCELLATION_SEARCH_CREATION_CONTENT,
        params: {
          page,
          limit,
        },
        data: {
          signatures: search.files
            .map((file) => file.signature)
            .filter((signature) => !!signature) as string[],
        },
      })

      const savedParams = {
        ...search,
        status: 'fetched',
      } as GiftShopSearchCreationState
      if (search.status !== 'fetched') setSearch(savedParams)
      update('searchCreationState', savedParams)

      if (
        typeof response === 'string' &&
        response === SERVICE_CANCELLED_RESPONSE
      ) {
        return undefined
      }

      return typeof response !== 'string' && response?.status === 200
        ? response.data.result.data
        : []
    },
    [search, setSearch, update],
  )

  const handleClickItem = useCallback(
    (item: GiftShopContentData) => {
      history.push(
        'giftshop_profile_creation_detail',
        {
          data: [item],
          selectedItemId: item.content_id,
          limitContent: true,
        },
        item.creator_name,
      )
    },
    [history],
  )

  const handleRenderItem = useCallback(
    (item: GiftShopContentData) => (
      <TemplateSearchCreationItem onClickItem={handleClickItem} data={item} />
    ),
    [handleClickItem],
  )

  const handleRenderEmpty = useMemo(
    () => (
      <StyledNotFoundContainer>
        <StyledEmptyImg
          height="fit-content"
          src={emptyListImage}
          alt={emptyListImage}
        />
        <StyledEmptyMessage fontSize="l" fontWeight="bold">
          {translate('giftShop:searchCreationNotFoundTitle')}
        </StyledEmptyMessage>
        <StyledEmptyMessage fontSize="m" color="gray_3">
          {translate('giftShop:searchCreationByFileNotFoundMessage')}
        </StyledEmptyMessage>
      </StyledNotFoundContainer>
    ),
    [emptyListImage],
  )

  const handleResetSearch = useCallback(() => {
    const resetState = {
      method: 'file',
      files: [],
      lastQueryChange: new Date().getTime(),
      status: 'initial',
    } as GiftShopSearchCreationState
    setSearch(resetState)
    update('searchCreationState', resetState)
  }, [setSearch, update])

  const handleRenderInput = useMemo(
    () => (
      <StyledInputContainer>
        <StyledSignatureInput
          containerStyle={{
            width: '100%',
            ...(status === 'fetched'
              ? {
                  color: theme.primary_5,
                }
              : {
                  color: theme.black_1,
                }),
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.preventDefault()
            if (search.files.length > 0) setAttachedFileModal(true)
          }}
          inputContainerStyle={{
            height: convertUnit(42),
            ...(status === 'fetched'
              ? {
                  borderColor: theme.primary_5,
                  backgroundColor: theme.info_1,
                }
              : {
                  borderColor: theme.gray_1,
                  backgroundColor: theme.white_2,
                }),
          }}
          inputStyle={{
            height: convertUnit(42),
            ...(status === 'fetched'
              ? {
                  color: theme.primary_5,
                  backgroundColor: theme.info_1,
                }
              : {
                  color: theme.black_1,
                  backgroundColor: theme.white_2,
                }),
          }}
          leftIcon={
            <Icon
              type="image"
              color={status === 'fetched' ? 'primary_5' : 'black_1'}
            />
          }
          value={translate('giftShop:searchCreationByFileAttached', {
            count: search.files.length,
          })}
          readOnly
        />
        <StyledSignatureReset
          iconType="close"
          onClick={() => {
            setResetSearchModal(true)
          }}
          iconColor={status === 'fetched' ? undefined : 'white_1'}
          iconDisabledColor="gray_3"
          disabled={status !== 'fetched'}
        />
      </StyledInputContainer>
    ),
    [search, status, theme],
  )

  const renderLoading = useCallback(
    (style?: CSSProperties, caption?: string) => (
      <StyledLoadingContainer style={style}>
        <StyledLottie animationData={animationData} />
        <Paragraph color="gray_5" fontSize={15}>
          {caption}
        </Paragraph>
      </StyledLoadingContainer>
    ),
    [],
  )

  const handleMakeFileSignatures = useCallback(
    async (files: File[]) => {
      const filesWithSignatures = files.map((f) => ({
        file: f,
        signature: undefined,
      })) as {file: File; signature?: string}[]

      setSearch((prev) => ({
        ...prev,
        method: 'file',
        files: filesWithSignatures,
        status: 'signature',
      }))

      let idx = 0
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const signature = await handleGetFileSignature(file)
        if (signature) filesWithSignatures[idx].signature = signature
        idx += 1
      }

      setSearch({
        method: 'file',
        files: filesWithSignatures,
        status: 'fetching',
        lastQueryChange: new Date().getTime(),
      })
    },
    [setSearch],
  )

  const handleValidateFiles = useCallback(
    (
      source: File[],
      maxUpload = GIFTSHOP_SEARCH_CREATION_BY_FILE_LIMIT_UPLOAD,
    ) => {
      let hasInvalidData = false
      const filterFile = (file: File) => {
        let {type} = file
        type = getAndSupplyFileMime(GIFT_SHOP_UPLOAD_TYPE, file)
        const isValid = GIFT_SHOP_UPLOAD_TYPE.includes(type)

        if (!isValid) {
          hasInvalidData = true
        }

        return isValid
      }
      const filter = source.filter(filterFile)
      const files = filter.splice(0, maxUpload)
      return {
        isInvalid: hasInvalidData,
        isFiltered: files.length !== source.length,
        files,
      }
    },
    [],
  )

  const renderUploadZone = useMemo(
    () => (
      <StyledDropZoneContainer>
        <DragDrop
          icon="upload"
          header={
            <Paragraph
              fontSize={15}
              fontWeight="bold"
              color="primary_5"
              style={{
                textAlign: 'center',
              }}>
              {translate('giftShop:searchCreationByFileInstruction')}
            </Paragraph>
          }
          description={
            <>
              <Paragraph color="primary_5">
                {translate('giftShop:maxFiles', {
                  count: GIFTSHOP_SEARCH_CREATION_BY_FILE_LIMIT_UPLOAD,
                })}
              </Paragraph>
              <StyledDropZoneButton>
                <Icon type="image" color="white_1" />
                <Paragraph fontSize={15} fontWeight="bold" color="white_1">
                  {translate('giftShop:selectFile')}
                </Paragraph>
              </StyledDropZoneButton>
            </>
          }
          onDrop={async (source) => {
            const {files, isFiltered, isInvalid} = handleValidateFiles(source)

            if (isInvalid || source.length < 1) {
              showSnackbar(translate('giftShop:invalidFormatData'))
              return
            }

            if (isFiltered) {
              showSnackbar(
                translate('giftShop:uploadMaximumMessage', {
                  max: GIFTSHOP_SEARCH_CREATION_BY_FILE_LIMIT_UPLOAD,
                }),
              )
            }

            await handleMakeFileSignatures(files)
          }}
        />
      </StyledDropZoneContainer>
    ),
    [handleMakeFileSignatures, handleValidateFiles],
  )

  const handleRenderContent = useMemo(() => {
    if (status === 'initial') {
      return renderUploadZone
    }

    const caption = (() => {
      if (status === 'invalid-input')
        return translate('giftShop:searchCreationPlaceholder', {
          count: GIFTSHOP_SEARCH_CREATION_CHARACTER_MIN,
        })
      if (status === 'signature')
        return translate('giftShop:searchCreationByFileAttaching', {
          count: search.files.length,
        })
      return translate('giftShop:searchCreationSearching')
    })()

    return (
      <StyledListSearchByTitleContainer>
        <ListLazy
          cache="search_content"
          stateData={stateData}
          keyExtractor={handleKeyExtractor}
          renderItem={handleRenderItem}
          loadData={handleLoadData}
          search={lastQueryChange}
          backgroundColor="white_1"
          listEmptyElement={handleRenderEmpty}
          outerMostContainerStyle={{
            overflowY: 'auto',
          }}
        />
        {status !== 'fetched' &&
          renderLoading(
            {
              position: 'absolute',
              left: 0,
              top: 0,
              width: 'inherit',
              height: 'inherit',
            },
            caption,
          )}
      </StyledListSearchByTitleContainer>
    )
  }, [
    status,
    stateData,
    handleKeyExtractor,
    handleRenderItem,
    handleLoadData,
    lastQueryChange,
    handleRenderEmpty,
    renderLoading,
    renderUploadZone,
    search,
  ])

  return (
    <>
      {handleRenderInput}
      <Paragraph
        color="gray_5"
        style={{
          marginBottom: convertUnit(8),
        }}>
        {translate('giftShop:searchCreationByFileNotice')}
      </Paragraph>
      {handleRenderContent}
      <GiftShopTemplateSearchCreationModal
        files={search.files}
        foundSignatures={foundSignatures}
        toggleModal={() => {
          setAttachedFileModal((prev) => !prev)
        }}
        visible={attachedFileModal}
      />
      <GiftShopTemplateSearchCreationResetModal
        onConfirm={() => {
          handleResetSearch()
        }}
        toggleModal={() => {
          setResetSearchModal((prev) => !prev)
        }}
        visible={resetSearchModal}
      />
    </>
  )
}
